import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import Dialog from '@pelckmans/elementis/components/dialog';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import FailureIcon from '@pelckmans/elementis/icons/failure';
import SuccessIcon from '@pelckmans/elementis/icons/success';
import TasksIcon from '@pelckmans/elementis/icons/tasks';
import UserIcon from '@pelckmans/elementis/icons/user';
import { ComponentProps, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../../api';
import { getExerciseAssignmentSession } from '../../../../api/material';
import ModalContext from '../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { MaterialIcon } from '../../../../pages/material/components/MaterialIcon';
import { assignmentsEndIn24hours, assignmentsEndIn7Days } from '../../../../pages/material/utils/assignmentUtils';
import { formatAssignmentToDate } from '../../../../utils/formatDate';
import { TOKEN_ACCESSOR } from '../../../../utils/tokenAccessor';
import LoadingState from '../../../LoadingState';

import './material-assignment.css';

type FormError = 'PRECHECK' | 'SUBMIT' | null;

type FinishedStatus = 'COMPLETED' | 'LATE' | 'NOT_FINISHED';

interface Material {
  id: string;
  name: string;
  contentType?: {
    name: string;
    icon?: string;
    iconClassName?: string;
  };
  isDone: boolean;
  type: 'medialink' | 'user-material';
  href?: string;
}

export type MaterialAssignment = Material & {
  moduleId: string;
  assignment: {
    id: string;
    to: string;
  };
  owner: {
    firstName: string;
    lastName: string;
  };
};

interface Props {
  id: string;
  material: Omit<MaterialAssignment, 'isDone'>;
  onOpenMaterial: () => void;
  onModalClosed: (material: Material) => void;
}

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.STUDENT.ASSIGNMENTS.MATERIAL_ASSIGNMENTS.DIALOG';
const studioGroup = process.env.REACT_APP_P_STUDIO_GROUP;

function MaterialAssignmentModal({ id, material, onOpenMaterial, onModalClosed }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();

  const [opened, setOpened] = useState(false);
  const [checked, setChecked] = useState(false);
  const [formError, setFormError] = useState<FormError>(null);
  const [finishedStatus, setFinishedStatus] = useState<FinishedStatus>('NOT_FINISHED');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  useEffect(() => {
    async function fetchExerciseAssignmentSession() {
      setIsLoading(true);

      const { result } = await getExerciseAssignmentSession(material.moduleId, material.assignment.id);

      if (result) setFinishedStatus(result.score?.achieved === 0 ? 'LATE' : 'COMPLETED');

      setIsLoading(false);
    }

    fetchExerciseAssignmentSession().catch(handleError);
  }, [material, handleError]);

  function handleClose() {
    closeModal(id);
  }

  async function handleSubmit() {
    const token = await TOKEN_ACCESSOR.tokenGetter();
    const headers = { Authorization: `Bearer ${token}`, studioGroup };

    try {
      await api.post(
        `/studio/user/modules/${material.moduleId}/assignments/${material.assignment.id}/exercise-session/closed`,
        undefined,
        { headers },
      );
    } catch (e) {
      handleError(e);
    } finally {
      closeModal(id);
      onModalClosed({ ...material, isDone: checked });
    }
  }

  function handleCheckboxChange() {
    if (opened) {
      setChecked(!checked);
      setFormError(null);
    } else {
      setFormError('PRECHECK');
    }
  }

  function handleSubmitClick() {
    if (isLoadingSubmit) return;

    if (opened && checked) {
      setIsLoadingSubmit(true);
      handleSubmit();
    } else {
      setFormError('SUBMIT');
    }
  }

  function handleOpen() {
    setOpened(true);
    setFormError(null);
  }

  const renderAlert = () => {
    if (formError === 'PRECHECK') {
      return <Alert prominence="warning">{t(`${BASE}.ALERT.PRECHECK`)}</Alert>;
    }
    if (formError === 'SUBMIT') {
      return <Alert prominence="danger">{t(`${BASE}.ALERT.SUBMIT`)}</Alert>;
    }
    return null;
  };

  const pillProminence: ComponentProps<typeof Pill>['prominence'] = useMemo(() => {
    if (assignmentsEndIn24hours(material.assignment)) {
      return 'danger';
    } else if (assignmentsEndIn7Days(material.assignment)) {
      return 'warning';
    }

    return 'neutral';
  }, [material.assignment]);

  return (
    <Dialog id={id} modal onClose={handleClose} size="md" aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header
        title={material.name}
        onClose={handleClose}
        aria-label={t(`${BASE}.CLOSE.A11Y_LABEL`)}
        meta={
          <PillGroup role={undefined} aria-label={undefined}>
            <Pill icon={<TasksIcon />}>{t('PAGES.TOC_LEVEL.MATERIAL_PILLS.ASSIGNMENT')}</Pill>
            <Pill prominence={pillProminence}>
              {t(`${BASE}.TO`, { to: formatAssignmentToDate(material.assignment.to) })}
            </Pill>
            <Pill icon={<UserIcon />}>{`${material.owner.firstName} ${material.owner.lastName}`}</Pill>
          </PillGroup>
        }
      />
      <Dialog.Body
        alertGroupProps={
          formError && !isLoading
            ? {
                'aria-label': undefined,
                role: undefined,
                children: renderAlert()!,
              }
            : undefined
        }
      >
        {isLoading ? (
          <LoadingState size={2} variant="list" aria-label={t(`${BASE}.LIST.LOADING.A11Y_LABEL`)} />
        ) : (
          <List>
            <List.Header title={t(`${BASE}.LIST.TITLE`)} titleTag="h4" />
            <List.Item
              icon={<MaterialIcon materialType={material.type} contentType={material.contentType} />}
              key={material.id}
              title={material.name}
              actionArea={
                <div className="material-assignment-action-area">
                  {material.href ? ( // logic of href is in useMedialink and useUserMaterial
                    <Button
                      key="open-link"
                      prominence="neutral"
                      href={material.href!}
                      as="link"
                      size="sm"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        handleOpen();
                        onOpenMaterial();
                      }}
                    >
                      {t(`${BASE}.LIST.OPEN`)}
                    </Button>
                  ) : (
                    <Button
                      key="open"
                      size="sm"
                      prominence="neutral"
                      onClick={() => {
                        handleOpen();
                        onOpenMaterial();
                      }}
                    >
                      {t(`${BASE}.LIST.OPEN`)}
                    </Button>
                  )}

                  {finishedStatus === 'COMPLETED' ? (
                    <Pill key="completed" prominence="success" icon={<SuccessIcon />} role="status">
                      {t(`${BASE}.LIST.COMPLETED`)}
                    </Pill>
                  ) : finishedStatus === 'LATE' ? (
                    <Pill key="late" prominence="danger" icon={<FailureIcon />} role="status">
                      {t(`${BASE}.LIST.LATE`)}
                    </Pill>
                  ) : (
                    <Checkbox
                      key="complete"
                      checked={checked}
                      error={formError === 'SUBMIT'}
                      onChange={handleCheckboxChange}
                    >
                      {t(`${BASE}.LIST.DONE`)}
                    </Checkbox>
                  )}
                </div>
              }
            />
          </List>
        )}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          role: undefined,
          'aria-label': undefined,
          children: (
            <>
              <Button key="cancel" prominence="neutral" onClick={handleClose}>
                {t('ACTIONS.CANCEL')}
              </Button>
              {finishedStatus === 'NOT_FINISHED' && !isLoading && (
                <Button key="submit" prominence="success" onClick={handleSubmitClick} disabled={isLoadingSubmit}>
                  {t(`${BASE}.COMPLETE`)}
                </Button>
              )}
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default MaterialAssignmentModal;
